import { useToast } from '@/contexts/ToastContext';
import { useSupaSocket } from '@/hooks/useSupaSocket';
import {
    WorkspaceMember as APIWorkspaceMember,
    featureService,
    PendingWorkspaceInvite,
    WorkspaceInvitation
} from '@common/api/featureService';
import { useUserStorage } from '@common/hooks/useUserStorage';
import {
    useCallback, useEffect, useState
} from 'react';
import { MemberStatus, WorkspaceRole } from './useWorkspaces';

export type {
    MemberStatus, PendingWorkspaceInvite, WorkspaceInvitation, APIWorkspaceMember as WorkspaceMember, WorkspaceRole
};

// 计算属性：用于 UI 显示的成员信息
export interface WorkspaceMemberDisplay extends APIWorkspaceMember {
    displayName: string;
    avatar: string;
    isCurrentUser: boolean;
}

// Hook 返回类型
export interface UseWorkspaceMembersReturn {
    members: WorkspaceMemberDisplay[];
    invitations: WorkspaceInvitation[];
    pendingInvites: PendingWorkspaceInvite[];
    isLoading: boolean;
    isLoadingInvitations: boolean;
    error: Error | null;
    refresh: () => Promise<void>;
    // CRUD 操作
    inviteMember: (email: string, role: WorkspaceRole) => Promise<void>;
    removeMember: (memberId: string) => Promise<void>;
    updateMemberRole: (memberId: string, role: WorkspaceRole) => Promise<void>;
    // 当前用户在当前 workspace 的角色
    currentUserRole: WorkspaceRole | null;
    canManageMembers: boolean;
}

/**
 * 从 workspace_members 表拉取和管理当前 workspace 的成员列表
 */
export const useWorkspaceMembers = (workspaceId?: string, currentUserId?: string, userEmail?: string): UseWorkspaceMembersReturn => {
    const [members, setMembers] = useState<WorkspaceMemberDisplay[]>([]);
    const [invitations, setInvitations] = useState<WorkspaceInvitation[]>([]);
    const [pendingInvites, setPendingInvites] = useState<PendingWorkspaceInvite[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingInvitations, setIsLoadingInvitations] = useState(true);
    const [error, setError] = useState<Error | null>(null);
    const [currentUserRole, setCurrentUserRole] = useState<WorkspaceRole | null>(null);
    const { get, set } = useUserStorage();
    

    const { showToast } = useToast();

    // 监听 workspace_members 表的 UPDATE 事件
    useSupaSocket(
        {
            table: 'workspace_members',
            schema: 'public',
            event: 'UPDATE',
            enabled: !!workspaceId && !!currentUserId,
            filter: `workspace_id=eq.${workspaceId}`
        },
        {
            onUpdate: (payload) => {
                const updatedMember = payload.new as any;

                // 检查是否是其他成员的 status 被修改
                if (updatedMember.status === 'REMOVED') {
                    // 从 members 列表中移除该成员
                    setMembers(prev => prev.filter(m => m.id !== updatedMember.id));

                    // 如果是当前用户被移除，显示提示并刷新页面
                    if (updatedMember.user_id === currentUserId) {
                        set("currentWorkspaceId", "");
                        showToast('You have been removed from this workspace', 'error');
                        setTimeout(() => {
                            window.location.reload();
                        }, 1500);
                    }
                }
            }
        }
    );

    /**
     * 从后端 API 拉取当前用户的待处理邀请列表
     */
    const fetchPendingInvitations = useCallback(async (email?: string) => {
        if (!email) {
            setInvitations([]);
            return;
        }

        setIsLoadingInvitations(true);
        try {
            // Api.request 会自动提取 response.data.data，所以 response 直接就是邀请数组
            const invitationsData = await featureService.getPendingInvitations(email);

            // response 已经是数组了
            setInvitations(Array.isArray(invitationsData) ? invitationsData : []);
        } catch (err: any) {
            setInvitations([]);
        } finally {
            setIsLoadingInvitations(false);
        }
    }, []);

    /**
     * 从后端 API 拉取当前 workspace 的成员列表
     */
    const fetchMembers = useCallback(async () => {
        if (!workspaceId) {
            setMembers([]);
            setPendingInvites([]);
            setCurrentUserRole(null);
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            // 调用后端 API 获取成员列表
            const response = await featureService.getWorkspaceMembers(workspaceId);

            // 新的 API 返回结构: { members, pendingInvites }
            const membersData = response.members || [];
            const pendingInvitesData = response.pendingInvites || [];

            // 转换成员数据为显示格式
            const formattedMembers: WorkspaceMemberDisplay[] = membersData.map((member: any) => {
                const name = member.name || member.email?.split('@')[0] || 'Unknown';
                const avatar = member.logoUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=1A162F&color=fff`;

                return {
                    ...member,
                    displayName: name,
                    avatar: avatar,
                    isCurrentUser: currentUserId ? member.userId === currentUserId : false,
                };
            });

            setMembers(formattedMembers);
            setPendingInvites(pendingInvitesData);

            // 找出当前用户的角色
            const currentMember = formattedMembers.find(m => m.isCurrentUser);
            setCurrentUserRole(currentMember?.role || null);

        } catch (err: any) {
            setError(err);
            setMembers([]);
            setPendingInvites([]);
            setCurrentUserRole(null);
        } finally {
            setIsLoading(false);
        }
    }, [workspaceId, currentUserId]);

    // 初始化和 workspace 变化时拉取数据
    useEffect(() => {
        fetchMembers();
    }, [fetchMembers]);

    // 单独处理邀请列表，只在 userEmail 变化时调用
    useEffect(() => {
        fetchPendingInvitations(userEmail);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userEmail]);

    /**
     * 邀请新成员到 workspace
     */
    const inviteMember = useCallback(async (email: string, role: WorkspaceRole) => {
        if (!workspaceId) {
            throw new Error('No workspace selected');
        }

        setIsLoading(true);
        setError(null);

        try {
            // 调用后端 API 发送邀请
            const response = await featureService.inviteWorkspaceMember(workspaceId, {
                email,
                role,
            });

            // 刷新成员列表
            await fetchMembers();
        } catch (err: any) {
            setError(err);
            throw err;
        } finally {
            setIsLoading(false);
        }
    }, [workspaceId, fetchMembers]);

    /**
     * 从 workspace 移除成员
     */
    const removeMember = useCallback(async (memberId: string) => {
        if (!workspaceId) {
            throw new Error('No workspace selected');
        }

        setIsLoading(true);
        setError(null);

        try {
            // 调用后端 API 移除成员
            const response = await featureService.removeWorkspaceMember(workspaceId, memberId);

            // 刷新成员列表
            await fetchMembers();
        } catch (err: any) {
            setError(err);
            throw err;
        } finally {
            setIsLoading(false);
        }
    }, [workspaceId, fetchMembers]);

    /**
     * 更新 workspace 成员的角色
     */
    const updateMemberRole = useCallback(async (memberId: string, role: WorkspaceRole) => {
        if (!workspaceId) {
            throw new Error('No workspace selected');
        }

        setIsLoading(true);
        setError(null);

        try {
            // 调用后端 API 更新成员角色
            const response = await featureService.updateWorkspaceMemberRole(
                workspaceId,
                memberId,
                { role }
            );

            // 刷新成员列表
            await fetchMembers();
        } catch (err: any) {
            setError(err);
            throw err;
        } finally {
            setIsLoading(false);
        }
    }, [workspaceId, fetchMembers]);

    /**
     * 判断当前用户是否可以管理成员
     * Owner 和 Admin 可以管理成员
     */
    const canManageMembers = currentUserRole === 'OWNER' || currentUserRole === 'ADMIN';

    return {
        members,
        invitations,
        pendingInvites,
        isLoading,
        isLoadingInvitations,
        error,
        refresh: fetchMembers,
        inviteMember,
        removeMember,
        updateMemberRole,
        currentUserRole,
        canManageMembers,
    };
};
